import React, { useState } from 'react';
import { FormField } from './FormField';
import { SearchIcon, CreditCardIcon, AlertCircleIcon } from 'lucide-react';
import { MetadataItem } from './MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface CardOnFilePaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
}
export function CardOnFilePayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange
}: CardOnFilePaymentProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const handleExpiryDateChange = (date: string) => {
    setExpiryDate(date);
    if (onExpiryDateChange) {
      onExpiryDateChange(date);
    }
  };
  const handleAmountChange = (newAmount: string) => {
    if (onAmountChange) {
      onAmountChange(newAmount);
    }
  };
  const handleCurrencyChange = (newCurrency: string) => {
    if (onCurrencyChange) {
      onCurrencyChange(newCurrency);
    }
  };
  const handleReferenceChange = (newReference: string) => {
    if (onReferenceChange) {
      onReferenceChange(newReference);
    }
  };
  // Mock data for saved cards
  const savedCards = [{
    id: 1,
    customer: 'John Smith',
    email: 'john@example.com',
    cardLast4: '4242',
    cardBrand: 'Visa',
    expiryDate: '12/24'
  }, {
    id: 2,
    customer: 'Sarah Johnson',
    email: 'sarah@example.com',
    cardLast4: '1234',
    cardBrand: 'Mastercard',
    expiryDate: '09/25'
  }];
  const filteredCards = savedCards.filter(card => card.customer.toLowerCase().includes(searchTerm.toLowerCase()) || card.email.toLowerCase().includes(searchTerm.toLowerCase()));
  return <div className="space-y-6" data-id="element-160">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-161">
        <h2 className="text-lg font-medium text-gray-800 mb-4 flex items-center" data-id="element-162">
          <CreditCardIcon size={18} className="mr-2 text-blue-600" data-id="element-163" />
          Instant card charge
        </h2>
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6 flex items-start" data-id="element-164">
          <AlertCircleIcon size={18} className="text-yellow-600 mr-2 mt-0.5 flex-shrink-0" data-id="element-165" />
          <p className="text-sm text-yellow-800" data-id="element-166">
            This will immediately charge the selected card. The customer will
            receive a receipt by email but won't need to take any action.
          </p>
        </div>
        <FormField label="Find customer" tooltip="Search for customers with saved payment methods" data-id="element-167">
          <div className="relative" data-id="element-168">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-169">
              <SearchIcon size={18} className="text-gray-400" data-id="element-170" />
            </div>
            <input type="text" className="block w-full pl-10 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search by name or email" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-171" />
          </div>
        </FormField>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-172">
        <h3 className="text-sm font-medium text-gray-700 mb-2" data-id="element-173">
          Saved payment methods
        </h3>
        {filteredCards.length > 0 ? <div className="space-y-2" data-id="element-174">
            {filteredCards.map(card => <div key={card.id} className="border border-gray-200 rounded-md p-3 hover:border-blue-500 cursor-pointer" data-id="element-175">
                <div className="flex items-center" data-id="element-176">
                  <div className="mr-3" data-id="element-177">
                    <input type="radio" name="savedCard" id={`card-${card.id}`} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-178" />
                  </div>
                  <label htmlFor={`card-${card.id}`} className="flex-1 cursor-pointer" data-id="element-179">
                    <div className="flex justify-between" data-id="element-180">
                      <span className="font-medium" data-id="element-181">{card.customer}</span>
                      <div className="flex items-center text-gray-600" data-id="element-182">
                        <CreditCardIcon size={16} className="mr-1" data-id="element-183" />
                        <span className="text-sm" data-id="element-184">
                          {card.cardBrand} •••• {card.cardLast4}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 mt-1" data-id="element-185">
                      <div data-id="element-186">{card.email}</div>
                      <div className="text-xs mt-1" data-id="element-187">
                        Expires: {card.expiryDate}
                      </div>
                    </div>
                  </label>
                </div>
              </div>)}
          </div> : <div className="text-center py-6 bg-gray-50 rounded-md" data-id="element-188">
            <p className="text-gray-500" data-id="element-189">No saved cards found</p>
            <p className="text-sm text-gray-400 mt-1" data-id="element-190">
              Try a different search term
            </p>
          </div>}
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-191">
        <FormField label="Payment amount" required tooltip="The amount to charge to the selected card" data-id="element-192">
          <div className="relative" data-id="element-193">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-194">
              <span className="text-gray-500" data-id="element-195">$</span>
            </div>
            <input type="text" className="block w-full pl-8 pr-12 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="0.00" onChange={e => handleAmountChange(e.target.value)} data-id="element-196" />
            <div className="absolute inset-y-0 right-0 flex items-center" data-id="element-197">
              <select className="h-full py-0 pl-2 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm rounded-md focus:ring-blue-500 focus:border-blue-500" onChange={e => handleCurrencyChange(e.target.value)} data-id="element-198">
                <option data-id="element-199">USD</option>
                <option data-id="element-200">EUR</option>
                <option data-id="element-201">GBP</option>
              </select>
            </div>
          </div>
        </FormField>
        <FormField label="Description" tooltip="A brief description of what this charge is for" data-id="element-202">
          <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="e.g., Monthly subscription or Invoice #1234" onChange={e => handleReferenceChange(e.target.value)} data-id="element-203" />
        </FormField>
      </div>
      <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} hideRedirect={true} data-id="element-204" />
    </div>;
}